import React, { Dispatch, ReactNode, useEffect, useReducer, useState } from 'react';
import cn from 'classnames';
import styles from './PaymentPanel.module.scss';
import { CheckmarkIcon } from '../SelectAccountTypePanel';
import { IAPProduct, IAPTransactionEvent, IAPTransactionState } from '@repo/utils/types';
import { capitalize } from '@repo/utils/helpers/string';
import { LoaderIcon } from '@repo/icons';
import Action from '@components/Button/Action';
import { apiRequest } from '@repo/utils/helpers/api';

/*
TODO:
- We'll want to move the onTransactionUpdated handler to update Redux state so that we are always listening
  for any transaction updates. This state will be used for upgrade UI in Settings.
*/

const IAPOptionButton = ({
  product,
  setSelectedProductID,
  selected,
}: {
  product: IAPProduct;
  setSelectedProductID: Dispatch<string>;
  selected?: boolean;
}) => (
  <button
    className={cn(styles.iapOptionBtn, selected && styles.selected)}
    onClick={() => setSelectedProductID(product.productIdentifier)}
  >
    <div className={styles.title}>
      <span>{capitalize(product.subscriptionPeriod.unit)}ly</span>
      {product.subscriptionPeriod.unit === 'year' && (
        <div className={styles.discount}>Save 50%</div>
      )}
    </div>
    <div className={styles.cost}>
      {product.formattedPrice} / {product.subscriptionPeriod.unit}
    </div>
    <div className={styles.checkbox}>
      <CheckmarkIcon className={styles.checkmark} />
    </div>
  </button>
);

const Loader = () => <LoaderIcon customColor="#000" r="1" />;

interface IAPOptionsState {
  buttonDisabled: boolean;
  buttonText: ReactNode;
  finished: boolean;
}

const defaultIAPOptionsState: IAPOptionsState = {
  buttonDisabled: false,
  buttonText: 'Start 14 day free trial',
  finished: false,
};

const iapOptionsStateReducer = (
  state: IAPOptionsState,
  transactionState: IAPTransactionState,
): IAPOptionsState => {
  switch (transactionState) {
    case 'purchasing':
      return { ...state, buttonDisabled: true, buttonText: <Loader /> };

    case 'purchased':
    case 'restored':
      return {
        ...state,
        buttonDisabled: true,
        buttonText: (
          <>
            We're wrapping up! <Loader />
          </>
        ),
      };

    case 'failed':
      return {
        ...state,
        buttonDisabled: false,
        buttonText: 'Transaction failed, please try again',
      };

    // case 'deferred'

    case 'finished':
      return { ...state, finished: true };

    default:
      return { ...defaultIAPOptionsState };
  }
};

export default function IAPOptions({
  iAPProducts,
  onProductPurchased,
  setPaymentCadence,
  isUpgradeModal = false,
}: {
  iAPProducts: IAPProduct[];
  onProductPurchased: () => void;
  setPaymentCadence?: Dispatch<'monthly' | 'annually'>;
  isUpgradeModal?: boolean;
}) {
  const [canMakePayments, setCanMakePayments] = useState(false);
  const [selectedProductID, setSelectedProductID] = useState(iAPProducts[0].productIdentifier);
  const [{ buttonText, buttonDisabled, finished }, componentStateReducer] = useReducer(
    iapOptionsStateReducer,
    defaultIAPOptionsState,
  );

  const purchaseProduct = () => {
    window.desktopIntegrations.iap.purchaseProduct(selectedProductID, {});
  };

  const restoreCompletedTransactions = () => {
    window.desktopIntegrations.iap.restoreCompletedTransactions();
  };

  const btnProps = {
    onClick: purchaseProduct,
    disabled: buttonDisabled,
    children: buttonText,
  };

  useEffect(() => {
    let unsub;

    if (window.app.isElectron && window.desktopIntegrations?.iap?.onTransactionUpdated) {
      unsub = window.desktopIntegrations.iap.onTransactionUpdated((update: IAPTransactionEvent) => {
        // if (['purchased', 'restored'].includes(update.transactionState)) {
        if (['purchased'].includes(update.transactionState)) {
          // send the event information to API to track the purchase
          apiRequest(
            'POST',
            '/billing/revenuecat/register/',
            {
              transaction_id: update.transactionIdentifier,
              original_transaction_id: update.originalTransactionIdentifier,
              product_identifier: update.payment.productIdentifier,
            },
            {
              apiVersion: 'v2',
            },
          );
        }

        componentStateReducer(update.transactionState);
      });
    }

    return () => unsub?.();
  }, []);

  useEffect(() => {
    if (finished) {
      onProductPurchased();
    }
  }, [finished]);

  useEffect(() => {
    window.desktopIntegrations.iap.canMakePayments().then(setCanMakePayments);
  });

  useEffect(() => {
    setPaymentCadence?.('annually');
  }, []);

  return canMakePayments ? (
    <div className={styles.iAPOptions}>
      {iAPProducts.map((product) => (
        <IAPOptionButton
          key={product.productIdentifier}
          product={product}
          setSelectedProductID={(productID) => {
            setSelectedProductID(productID);

            if (productID === 'com.clay.membership.annual') {
              setPaymentCadence?.('annually');
            } else {
              setPaymentCadence?.('monthly');
            }
          }}
          selected={product.productIdentifier === selectedProductID}
        />
      ))}
      <button className={styles.restorePurchasesBtn} onClick={restoreCompletedTransactions}>
        Restore Purchases
      </button>
      {isUpgradeModal ? (
        <Action {...btnProps} />
      ) : (
        <button className={styles.purchaseBtn} {...btnProps} />
      )}
    </div>
  ) : null;
}
